import { PolicyStore } from '@Policy/store';
import i18nInst from 'src/i18n';
import {
  ActionData,
  ActionEnum,
  AsActionKeys,
  AsAddDisclaimerKeys,
  AsNotifyTypeKeys,
  AsSuspiciousTagKeys,
  ExchangeOnlinePolicyItem,
  MailPolicyNameEnum,
} from 'src/pages/Policy/utils/types';
import { policyTypeNameSelector } from '../general/store';

/*
 * selectors的作用域是store，这样可以同时获取action和policy，但要注意policy是在template中的
 * 这样做会造成心智负担： selector的作用域是整个store，而组件中和filterSlice中的作用域都是template(policy)
 * 在selector中，policy = store.template， 而组件中，直接看到的就是state=policy
 * 为了能在selector中同时获取policy和action，并且为了在组件和filterSlice中只关注policy，减少policy的层级深度，采取了这样的设计
 */
export const asActionDataSelector = (s: PolicyStore) => {
  const { t } = i18nInst;
  const {
    setAsState,
    template,
    config: {
      featureShow: { gmailQtSettingFeatureShow, spamActionSupportQt },
    },
  } = s;
  const {
    tmAntispamfilters: {
      asActionBec,
      asActionOthers,
      asActionPhish,
      asActionRansome,
      asActionGrayEmail,
      asActionMalicious,
      asActionBlockedSender,
      asActionScam,
      asActionSuspiciousSender,
      asBecNotifyType,
      asOthersNotifyType,
      asPhishNotifyType,
      asRansomeNotifyType,
      asMaliciousNotifyType,
      asBlockedSenderNotifyType,
      asGrayEmailNotifyType,
      asScamNotifyType,
      asSuspiciousSenderNotifyType,
      asSkipInternalSpamDetection,
      asSuspiciousTagBec,
      asSuspiciousTagOthers,
      asSuspiciousTagPhish,
      asSuspiciousTagRansome,
      asSuspiciousTagMalicious,
      asSuspiciousTagBlockedSender,
      asSuspiciousTagGrayEmail,
      asSuspiciousTagScam,
      asSuspiciousTagSuspiciousSender,
      asDisclaimerSuspiciousSender,
      asDisclaimerBec,
      asDisclaimerGrayEmail,
      asDisclaimerMalicious,
      asDisclaimerPhish,
      asDisclaimerOtherSpam,
      asDisclaimerRansome,
      asDisclaimerScam,
    },
  } = template as ExchangeOnlinePolicyItem;
  const policyTypeName = policyTypeNameSelector(s);
  let actions: ActionEnum[] = [];
  let actionOptionsSpam: ActionEnum[] = [];
  let actionOptionsBlockedSender: ActionEnum[] = [];
  let actionOptionsSuspiciousSender: ActionEnum[] = [];
  let actionOptionsBEC: ActionEnum[] = [];
  // Exchange
  const actionOptionsExchange = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.moveToJunkEmailFolder,
    ActionEnum.pass,
  ];
  const actionOptionsExchangeBEC = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.moveToJunkEmailFolder,
    ActionEnum.addDisclaimer,
    ActionEnum.pass,
  ];
  const actionOptionsSpamExchange = spamActionSupportQt
    ? [
        ActionEnum.tagSubject,
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.moveToJunkEmailFolder,
        ActionEnum.pass,
      ]
    : [
        ActionEnum.tagSubject,
        ActionEnum.delete,
        ActionEnum.moveToJunkEmailFolder,
        ActionEnum.pass,
      ];
  const actionOptionsBlockedSenderExchange = [
    ActionEnum.delete,
    ActionEnum.quarantine,
  ];
  const actionOptionsSuspiciousSenderExchange = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.moveToJunkEmailFolder,
    ActionEnum.addDisclaimer,
    ActionEnum.pass,
  ];
  // Exchange inline inbound
  const actionOptionsExchangeInlineInbound = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.moveToJunkEmailFolder,
    ActionEnum.addDisclaimer,
    ActionEnum.pass,
  ];
  const actionOptionsSpamExchangeInlineInbound = spamActionSupportQt
    ? [
        ActionEnum.tagSubject,
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.moveToJunkEmailFolder,
        ActionEnum.addDisclaimer,
        ActionEnum.pass,
      ]
    : [
        ActionEnum.tagSubject,
        ActionEnum.delete,
        ActionEnum.moveToJunkEmailFolder,
        ActionEnum.addDisclaimer,
        ActionEnum.pass,
      ];
  const actionOptionsBlockedSenderExchangeInlineInbound = [
    ActionEnum.delete,
    ActionEnum.quarantine,
  ];
  // Exchange inline outbound
  const actionOptionsSpamExchangeInlineOutbound = spamActionSupportQt
    ? [
        ActionEnum.tagSubject,
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.pass,
      ]
    : [ActionEnum.tagSubject, ActionEnum.delete, ActionEnum.pass];
  const actionOptionsExchangeInlineOutbound = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.pass,
  ];
  const actionOptionsBlockedSenderExchangeInlineOutbound = [
    ActionEnum.delete,
    ActionEnum.quarantine,
  ];
  // Gmail
  const actionOptionsGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.labelEmail,
        ActionEnum.moveToSpam,
        ActionEnum.pass,
      ]
    : [
        ActionEnum.delete,
        ActionEnum.labelEmail,
        ActionEnum.moveToSpam,
        ActionEnum.pass,
      ];
  const actionOptionsBlockedSenderGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.labelEmail,
        ActionEnum.moveToSpam,
      ]
    : [ActionEnum.delete, ActionEnum.labelEmail, ActionEnum.moveToSpam];
  const actionOptionsSpamGmail = actionOptionsGmail;
  // Gmail inline inbound
  const actionOptionsGmailInline = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.moveToSpam,
    ActionEnum.pass,
  ];
  const actionOptionsBlockedSenderGmailInline = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    // This option will be displayed for residual data from the past
    // see: src/pages/Policy/AdvancedThreatProtection/PolicyTemplate/Components/Actions.tsx modifiedActionData
    // ActionEnum.moveToSpam,
  ];
  const actionOptionsSpamGmailInline = actionOptionsGmailInline;
  // Gmail inline outbound
  const actionOptionsGmailInlineOutbound = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.pass,
  ];
  const actionOptionsBlockedSenderGmailInlineOutbound = [
    ActionEnum.delete,
    ActionEnum.quarantine,
  ];
  const actionOptionsSpamGmailInlineOutbound = actionOptionsGmailInlineOutbound;
  switch (policyTypeName) {
    case MailPolicyNameEnum.Exchange:
      actions = actionOptionsExchange;
      actionOptionsSpam = actionOptionsSpamExchange;
      actionOptionsBlockedSender = actionOptionsBlockedSenderExchange;
      actionOptionsSuspiciousSender = actionOptionsSuspiciousSenderExchange;
      actionOptionsBEC = actionOptionsExchangeBEC;
      break;
    case MailPolicyNameEnum.ExchangeInlineInbound:
      actions = actionOptionsExchangeInlineInbound;
      actionOptionsSpam = actionOptionsSpamExchangeInlineInbound;
      actionOptionsBlockedSender =
        actionOptionsBlockedSenderExchangeInlineInbound;
      actionOptionsBEC = actions;
      break;
    case MailPolicyNameEnum.ExchangeInlineOutbound:
      actions = actionOptionsExchangeInlineOutbound;
      actionOptionsSpam = actionOptionsSpamExchangeInlineOutbound;
      actionOptionsBlockedSender =
        actionOptionsBlockedSenderExchangeInlineOutbound;
      actionOptionsBEC = actions;
      break;
    case MailPolicyNameEnum.Gmail:
      actions = actionOptionsGmail;
      actionOptionsSpam = actionOptionsSpamGmail;
      actionOptionsBlockedSender = actionOptionsBlockedSenderGmail;
      actionOptionsBEC = actions;
      break;
    case MailPolicyNameEnum.GmailInlineInbound:
      actions = actionOptionsGmailInline;
      actionOptionsSpam = actionOptionsSpamGmailInline;
      actionOptionsBlockedSender = actionOptionsBlockedSenderGmailInline;
      actionOptionsBEC = actions;
      break;
    case MailPolicyNameEnum.GmailInlineOutbound:
      actions = actionOptionsGmailInlineOutbound;
      actionOptionsSpam = actionOptionsSpamGmailInlineOutbound;
      actionOptionsBlockedSender =
        actionOptionsBlockedSenderGmailInlineOutbound;
      actionOptionsBEC = actions;
      break;
    default:
      break;
  }
  let actionData: ActionData[] = [
    {
      threatType: t('policy.threatType.spam'),
      action: asActionOthers,
      actionKey: AsActionKeys.asActionOthers,
      handler: setAsState,
      actions: actionOptionsSpam,
      notifyType: asOthersNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asOthersNotifyType,
      suspiciousTag: asSuspiciousTagOthers,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagOthers,
      addDisclaimerValue: asDisclaimerOtherSpam,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerOtherSpam,
      otherSettings: {
        value: asSkipInternalSpamDetection,
        title: t('policy.threatType.spam.otherSettingsTitle'),
        otherSettingKey: 'asSkipInternalSpamDetection',
      },
    },
    {
      threatType: t('policy.threatType.maliciousContent'),
      info: t('policy.threatType.maliciousContent.info'),
      handler: setAsState,
      action: asActionMalicious,
      actionKey: AsActionKeys.asActionMalicious,
      notifyType: asMaliciousNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asMaliciousNotifyType,
      suspiciousTag: asSuspiciousTagMalicious,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagMalicious,
      addDisclaimerValue: asDisclaimerMalicious,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerMalicious,
      actions,
    },
    {
      threatType: t('policy.threatType.graymail'),
      handler: setAsState,
      action: asActionGrayEmail,
      actionKey: AsActionKeys.asActionGrayEmail,
      notifyType: asGrayEmailNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asGrayEmailNotifyType,
      suspiciousTag: asSuspiciousTagGrayEmail,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagGrayEmail,
      addDisclaimerValue: asDisclaimerGrayEmail,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerGrayEmail,
      actions,
    },
    {
      threatType: t('policy.threatType.scam'),
      info: t('policy.threatType.scam.info'),
      handler: setAsState,
      action: asActionScam,
      actionKey: AsActionKeys.asActionScam,
      notifyType: asScamNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asScamNotifyType,
      suspiciousTag: asSuspiciousTagScam,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagScam,
      addDisclaimerValue: asDisclaimerScam,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerScam,
      actions,
    },
    {
      threatType: t('policy.threatType.BEC'),
      handler: setAsState,
      action: asActionBec,
      actionKey: AsActionKeys.asActionBec,
      notifyType: asBecNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asBecNotifyType,
      suspiciousTag: asSuspiciousTagBec,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagBec,
      addDisclaimerValue: asDisclaimerBec,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerBec,
      actions: actionOptionsBEC,
    },
    {
      threatType: t('policy.threatType.phishing'),
      handler: setAsState,
      action: asActionPhish,
      actionKey: AsActionKeys.asActionPhish,
      notifyType: asPhishNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asPhishNotifyType,
      suspiciousTag: asSuspiciousTagPhish,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagPhish,
      addDisclaimerValue: asDisclaimerPhish,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerPhish,
      actions,
    },
    {
      threatType: t('policy.threatType.ransomware'),
      handler: setAsState,
      action: asActionRansome,
      actionKey: AsActionKeys.asActionRansome,
      notifyType: asRansomeNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asRansomeNotifyType,
      suspiciousTag: asSuspiciousTagRansome,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagRansome,
      addDisclaimerValue: asDisclaimerRansome,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerRansome,
      actions,
    },
    {
      threatType: t('policy.threatType.blockedSender'),
      handler: setAsState,
      action: asActionBlockedSender,
      actionKey: AsActionKeys.asActionBlockedSender,
      notifyType: asBlockedSenderNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asBlockedSenderNotifyType,
      suspiciousTag: asSuspiciousTagBlockedSender,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagBlockedSender,
      actions: actionOptionsBlockedSender,
    },
    {
      threatType: t('policy.threatType.suspiciousSender'),
      // info: t('policy.threatType.suspiciousSender.info'),
      handler: setAsState,
      action: asActionSuspiciousSender,
      actionKey: AsActionKeys.asActionSuspiciousSender,
      notifyType: asSuspiciousSenderNotifyType,
      notifyTypeKey: AsNotifyTypeKeys.asSuspiciousSenderNotifyType,
      suspiciousTag: asSuspiciousTagSuspiciousSender,
      suspiciousTagKey: AsSuspiciousTagKeys.asSuspiciousTagSuspiciousSender,
      addDisclaimerKey: AsAddDisclaimerKeys.asDisclaimerSuspiciousSender,
      addDisclaimerValue: asDisclaimerSuspiciousSender,
      actions: actionOptionsSuspiciousSender,
    },
  ];
  // Exchange inline inbound/Gmail/Gmail inline inbound: no Suspicious Sender
  // Exchange inline outbound/Gmail inline outbound：no Suspicious Sender, no BEC, no graymail
  switch (policyTypeName) {
    case MailPolicyNameEnum.ExchangeInlineInbound:
    case MailPolicyNameEnum.Gmail:
    case MailPolicyNameEnum.GmailInlineInbound:
      actionData = actionData.filter(
        (item) => item.actionKey !== AsActionKeys.asActionSuspiciousSender
      );
      break;
    case MailPolicyNameEnum.ExchangeInlineOutbound:
      actionData = actionData.filter((item) => {
        const exceptActionType: AsActionKeys[] = [
          AsActionKeys.asActionSuspiciousSender,
          AsActionKeys.asActionBec,
          AsActionKeys.asActionGrayEmail,
        ];
        return !exceptActionType.includes(item.actionKey as AsActionKeys);
      });
      break;
    case MailPolicyNameEnum.GmailInlineOutbound:
      actionData = actionData.filter((item) => {
        const exceptActionType: AsActionKeys[] = [
          AsActionKeys.asActionSuspiciousSender,
          AsActionKeys.asActionBec,
          AsActionKeys.asActionGrayEmail,
        ];
        return !exceptActionType.includes(item.actionKey as AsActionKeys);
      });
      // xuehai fix bug, why ???
      actionData.forEach((item) => {
        if (item.action === ActionEnum.moveToSpam) {
          item.action = ActionEnum.pass;
        }
      });
      break;
    default:
      break;
  }
  return actionData;
};

export const asApprovedHeaderListSelector = (s: PolicyStore) => {
  const template = s.template;
  const asApprovedHeaderList = (template as ExchangeOnlinePolicyItem)
    .tmAntispamfilters.asApprovedHeaderList;
  const str = asApprovedHeaderList.trim();
  if (str === '') {
    return [];
  }
  const list = JSON.parse(str);
  return list;
};

export const asBlockedHeaderListSelector = (s: PolicyStore) => {
  const template = s.template;
  const asBlockedHeaderRuleList = (template as ExchangeOnlinePolicyItem)
    .tmAntispamfilters.asBlockedHeaderRuleList;
  const str = asBlockedHeaderRuleList.trim();
  if (str === '') {
    return [];
  }
  const list = JSON.parse(str);
  return list;
};

export const asApprovedSenderEmailEntriesSelector = (s: PolicyStore) => {
  const template = s.template;
  const asApprovedSenderEmailEntries = (template as ExchangeOnlinePolicyItem)
    .tmAntispamfilters.asApprovedSenderEmailEntries;
  if (asApprovedSenderEmailEntries === '') {
    return [];
  }
  const list = asApprovedSenderEmailEntries.split(';').map((entry) => {
    return {
      value: entry,
      name: entry,
    };
  });
  return list;
};
export const asBlockedSenderEmailEntriesSelector = (s: PolicyStore) => {
  const template = s.template;
  const asBlockedSenderEmailEntries = (template as ExchangeOnlinePolicyItem)
    .tmAntispamfilters.asBlockedSenderEmailEntries;
  if (asBlockedSenderEmailEntries === '') {
    return [];
  }
  const list = asBlockedSenderEmailEntries.split(';').map((entry) => {
    return {
      value: entry,
      name: entry,
    };
  });
  return list;
};
